'use client';

import { useCallback, useRef, useState, useSyncExternalStore } from 'react';

import { Main } from '@ui/sections/main/Main';
import { useCurrentUser, useUserType } from '@website/hooks/useCurrentUser';
import { useSearchPosition } from '@website/hooks/useSearchPosition';

import { SearchFilters } from '../../../../components/SearchFilters';
import { ViewToggleFloatingButton } from '../../../../components/ViewToggleFloatingButton';
import ActivitiesListWrapper from './ActivitiesListWrapper';
import { SearchTopBar } from './SearchTopBar';

// Past this scroll offset, scrolling down collapses the visitor top bar (≈ its height).
const TOP_BAR_COLLAPSE_OFFSET = 56;
// Ignore jitter smaller than this when reading the scroll direction.
const SCROLL_DIRECTION_THRESHOLD = 8;

const subscribeToNothing = () => () => {};

interface ClientWrapperProps {
  categoryItems: Array<{ id: string; name: string }>;
}

export default function ClientWrapper({ categoryItems }: ClientWrapperProps) {
  const { data: user } = useCurrentUser();
  const [isMapDisplayMode, setIsMapDisplayMode] = useState(false);
  const [selectedActivityId, setSelectedActivityId] = useState<string | undefined>(undefined);
  const [selectionSource, setSelectionSource] = useState<'map' | 'list' | null>(null);
  const [isLocationPickerOpen, setIsLocationPickerOpen] = useState(false);
  const position = useSearchPosition();
  const userType = useUserType();
  // The page is statically rendered as a visitor: keep the top bar through
  // hydration so visitors (who land here from search engines) see no shift.
  const isHydrating = useSyncExternalStore(
    subscribeToNothing,
    () => false,
    () => true
  );
  const showTopBar = isHydrating || userType === 'public';
  const [isTopBarCollapsed, setIsTopBarCollapsed] = useState(false);
  const lastScrollTopRef = useRef(0);

  const handleResultsScroll = useCallback((event: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
    const delta = scrollTop - lastScrollTopRef.current;
    if (Math.abs(delta) < SCROLL_DIRECTION_THRESHOLD) return;
    lastScrollTopRef.current = scrollTop;
    if (delta < 0 || scrollTop <= TOP_BAR_COLLAPSE_OFFSET) {
      setIsTopBarCollapsed(false);
      return;
    }
    // Collapsing grows the results area; near the bottom that would clamp
    // scrollTop, read as a scroll up, and bounce the bar back open.
    if (scrollHeight - clientHeight - scrollTop > TOP_BAR_COLLAPSE_OFFSET) {
      setIsTopBarCollapsed(true);
    }
  }, []);

  const toggleViewMode = () => {
    setSelectedActivityId(undefined);
    setSelectionSource(null);
    setIsTopBarCollapsed(false);
    lastScrollTopRef.current = 0;
    setIsMapDisplayMode((prev) => !prev);
  };

  return (
    <div className="flex flex-col flex-1 min-h-0">
      <Main className="flex flex-col flex-1 min-h-0 relative w-full">
        <div className="flex-shrink-0 z-30 bg-white border-b border-neutral-200">
          {showTopBar && <SearchTopBar hidden={isTopBarCollapsed} />}
          <div className="py-2">
            <SearchFilters
              className="layout-bounds"
              categories={categoryItems}
              setLocation={position.setLocation}
              onLocationPickerOpenChange={setIsLocationPickerOpen}
            />
          </div>
        </div>
        <div className="relative flex-1 min-h-0 overflow-y-auto" onScroll={handleResultsScroll}>
          <ActivitiesListWrapper
            isMapDisplayMode={isMapDisplayMode}
            selectedActivityId={selectedActivityId}
            setSelectedActivityId={setSelectedActivityId}
            selectionSource={selectionSource}
            setSelectionSource={setSelectionSource}
            user={user ?? null}
            mapBounds={position.mapBounds}
            handleMapIdle={position.handleMapIdle}
            center={position.center}
            zoom={position.zoom}
            panVersion={position.panVersion}
            setLocation={position.setLocation}
          />
        </div>
        {!isLocationPickerOpen && (
          <ViewToggleFloatingButton isMapDisplayMode={isMapDisplayMode} toggleViewMode={toggleViewMode} />
        )}
      </Main>
    </div>
  );
}
